import { useEffect, useState, useRef, useCallback } from "react";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const POLL_MS = 60_000; // 60s

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "Ora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}g`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

const TYPE_ACCENT = {
  promotion: "border-l-emerald-400",
  demotion: "border-l-rose-400",
  vacation_approved: "border-l-emerald-400",
  vacation_rejected: "border-l-rose-400",
  training_updated: "border-l-sheriff-gold",
  disciplinary: "border-l-amber-400",
  resignation_status: "border-l-slate-400",
  event_rsvp: "border-l-sky-400",
  generic: "border-l-sheriff-gold/40",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const anchorRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // silenzioso, ritenta al prossimo poll
    }
  }, []);

  // Polling periodico + iniziale
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Chiudi il popover con Escape / click outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await fetchNotifications();
      setLoading(false);
    }
  };

  const markOne = async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* ignora */ }
  };

  const markAll = async () => {
    if (!unread) return;
    setMarkingAll(true);
    try {
      await api.post("/notifications/mark-all-read");
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
    } catch { /* ignora */ }
    finally { setMarkingAll(false); }
  };

  return (
    <div className="relative" ref={anchorRef}>
      <button
        onClick={toggleOpen}
        className="relative h-10 w-10 rounded-full border border-sheriff-gold/30 bg-sheriff-gold/10 hover:bg-sheriff-gold/20 hover:border-sheriff-gold/60 text-sheriff-gold flex items-center justify-center transition-all"
        aria-label="Notifiche"
        data-testid="notif-bell"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0A1F18]"
            data-testid="notif-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[min(92vw,380px)] rounded-2xl border border-sheriff-gold/30 bg-[#0F2620]/98 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-sheriff-gold/20">
            <div>
              <div className="font-heading text-sheriff-gold text-sm">Notifiche</div>
              <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/50">
                {unread} non lette
              </div>
            </div>
            <button
              onClick={markAll}
              disabled={!unread || markingAll}
              className="text-[10px] uppercase tracking-wider text-sheriff-gold/70 hover:text-sheriff-gold disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="notif-mark-all-read"
            >
              {markingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
              Segna tutte
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-sheriff-gold" /></div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sheriff-gold/50 text-sm">Nessuna notifica</div>
            ) : (
              <ul>
                {items.map((n) => {
                  const accent = TYPE_ACCENT[n.type] || TYPE_ACCENT.generic;
                  const Wrapper = n.link ? Link : "div";
                  const wrapperProps = n.link
                    ? { to: n.link, onClick: () => { markOne(n.id); setOpen(false); } }
                    : {};
                  return (
                    <li
                      key={n.id}
                      className={`border-b border-sheriff-gold/10 last:border-b-0 ${n.read ? "opacity-60" : ""}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <Wrapper
                        {...wrapperProps}
                        className={`block px-4 py-3 border-l-2 ${accent} hover:bg-sheriff-gold/[0.05] transition-colors cursor-pointer`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <div className="text-[#F5E6B8] font-medium text-sm truncate">{n.title}</div>
                              {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-sheriff-gold shrink-0" />}
                            </div>
                            {n.body && (
                              <div className="text-xs text-[#D4C480] mt-0.5 line-clamp-2">{n.body}</div>
                            )}
                            <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/50 mt-1">
                              {timeAgo(n.created_at)}
                            </div>
                          </div>
                          {!n.read && (
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); markOne(n.id); }}
                              className="text-sheriff-gold/50 hover:text-sheriff-gold p-1"
                              title="Segna come letta"
                              data-testid={`notif-mark-${n.id}`}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </Wrapper>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
